import CoreService from "@/services/core.service";
import ParentService from "@/services/parent.service";
import SocketService from "@/services/socket.service";
import { useConfigStore } from '@/stores/config';
import { useConversationStore } from '@/stores/conversation';
import { useNavigationStore } from '@/stores/navigation';
import { useSessionStore } from '@/stores/session';
import { useStatusStore } from '@/stores/status';
import Header from '@/components/layout/Header.vue';
import Footer from '@/components/layout/Footer.vue';
import RpcCalls from "@/components/RpcCalls.vue";
import ColorUtil from "@/utils/color.util";
import { DEFAULT_PRIMARY_COLOR } from "@/constants/app.constant";
import { Views } from "@/types/ConfigTypes";
import ConversationService from "@/services/conversation.service";
export default {
    name: 'App',
    components: {
        RpcCalls,
        Header,
        Footer
    },
    props: {},
    data() {
        return {};
    },
    setup() {
        return {
            coreService: CoreService,
            configStore: useConfigStore(),
            conversationStore: useConversationStore(),
            navigationStore: useNavigationStore(),
            sessionStore: useSessionStore(),
            statusStore: useStatusStore(),
        };
    },
    computed: {
        getCurrentViewClassName() {
            return this.coreService.getCurrentViewName().toLowerCase() + '__view';
        },
        isViewLoading() {
            return this.coreService.isViewLoading();
        }
    },
    methods: {},
    async created() {
        this.$logger.info('messenger v3 initialisation started');
        //todo: move to coreServe init
        const apiKey = ParentService.getApiKey();
        let resp;
        try {
            resp = await CoreService.init({
                hostInfo: ParentService.getRemoteHostInfo(),
                apiKey: apiKey,
                clientWidgetKey: apiKey,
                sessionData: ParentService.getSessionData()
            });
        }
        catch (err) {
            // messenger key not found or error
            ParentService.deactivate();
            this.$logger.error('Cannot connect to Grasp or Messenger key is invalid');
            return;
        }
        // messenger unpublished?
        if (!resp.data.enabled && !ParentService.isInPreviewMode()) {
            ParentService.deactivate();
            return;
        }
        // in preview settings mode, set the locale based on the default locale
        // setTimeout is required to make change of language to work
        if (ParentService.isInPreviewMode()) {
            setTimeout(() => {
                this.configStore.setLocale(resp.data.settings.defaultLocale);
                this.$root.$i18n.locale = resp.data.settings.defaultLocale;
            }, 10);
        }
        // gdpr
        // if(consentRequired()) {
        //   this.configStore.approveGDPR(ParentService.isGdprApproved())
        // }
        ParentService.setMessengerInitialised();
        await CoreService.registerOnlinePoller();
        // config (showPageSelectorFooter)
        this.configStore.updateSettings(resp.data.settings);
        // agent online status
        this.statusStore.setAgentOnline(resp.data.status.agentsOnline);
        // agent email address found flag
        if (resp.data.status.emailAddressFound)
            this.statusStore.setEmailAddressFound(resp.data.status.emailAddressFound);
        // branding
        if (!resp?.data?.settings.appearance.brandingEnabled) {
            this.configStore.setBrandingEnabled(false);
        }
        // locale message overrides
        if (resp?.data?.settings.localeMessages) {
            this.configStore.mergeLocaleMessages(resp.data.settings.localeMessages, resp.data.settings.mergeFields);
        }
        // set language if default language is set
        if (resp?.data?.settings.defaultLocale && resp?.data?.settings.defaultLocale !== '') {
            this.configStore.setLocale(resp?.data?.settings.defaultLocale);
        }
        this.configStore.setBrowser(ParentService.detectBrowser());
        // appearance
        if (!resp?.data?.settings.appearance.switchThemeEnabled) {
            this.configStore.setSwitchThemeEnabled(false);
        }
        if (resp?.data?.settings.appearance.hideResolvedConversations) {
            this.configStore.setHideResolvedConversations(true);
        }
        // prechatEnabled
        // if(resp?.data?.settings.conversation.prechatEnabled) {
        //   this.configStore.setPrechatEnabled(false)
        // }
        // if(! resp?.data?.settings.appearance.showPageSelectorFooter) {
        //   this.configStore.setSwitchThemeEnabled(false)
        // }
        // page selector
        this.navigationStore.initViewSelector(resp?.data?.settings.views);
        // colors
        const root = document.documentElement;
        const color = resp.data.settings.appearance.primaryColor || DEFAULT_PRIMARY_COLOR;
        const colorDarker = ColorUtil.pSBC(-0.20, color);
        const colorDarkest = ColorUtil.pSBC(-0.40, color);
        root.style.setProperty('--primary-color-lightest', ColorUtil.pSBC(0.75, color));
        root.style.setProperty('--primary-color-lighter', ColorUtil.pSBC(0.20, color));
        root.style.setProperty('--primary-color', color);
        root.style.setProperty('--primary-color-darker', colorDarker);
        root.style.setProperty('--primary-color-darkest', colorDarkest);
        let userId, sessionId;
        if (resp?.data?.sessionData) {
            // save is store
            userId = resp.data.sessionData.uid;
            sessionId = resp.data.sessionData.sid;
            this.sessionStore.setSessionVars(apiKey, userId, sessionId);
            // store in namespace as well
            ParentService.saveSessionData(resp.data.sessionData);
            // setup socketService (no connection made yet)
            SocketService.init(sessionId); // user-id
            this.$logger.info('pusher channel:', sessionId);
            await ConversationService.fetchList();
        }
        else {
            this.$logger.error('initialized failed, grasp service is unreachable ERR_CODE:500');
            return;
        }
        ParentService.setRemoteSettings({ primaryColor: color, primaryColorDarker: colorDarker, primaryColorDarkest: colorDarkest });
        // removes the modal spinner icon
        this.navigationStore.setLoadingState(false);
        this.$logger.info('v3 main view loaded');
        // // restore last view
        const settings = ParentService.getPersistedSettings();
        let destView, destParams;
        if (settings.messenger.currentView) {
            if (settings.messenger.currentView.name !== Views.PAGE) {
                // push the home view first but without changing the view so it acts only as
                // an entry so the user can press back arrow to go to the home view.
                this.navigationStore.replace(Views.PAGE, { id: 'home' });
            }
            destView = settings.messenger.currentView.name;
            destParams = settings.messenger.currentView.params;
        }
        else {
            // push to homepage view on startup
            destView = Views.PAGE;
            destParams = { id: 'home' };
        }
        CoreService.push(destView, destParams);
        // override initial actions (if any)
        // params initial action
        const initAction = ParentService.getInitialAction();
        if (initAction) {
            this.$logger.debug('action ' + initAction + ' found');
            let handler = null;
            switch (initAction) {
                case 'OPEN_HOME':
                    ParentService.openMessenger();
                    CoreService.push(Views.PAGE, { id: 'home' });
                    break;
                case 'OPEN_CHAT':
                case 'START_CHAT':
                    // opens last chat or start new if non-existent
                    handler = setInterval(() => {
                        // use setInterval as the conversation store might not be initialised
                        if (ConversationService.isStoreLoaded()) {
                            clearInterval(handler);
                            destView = Views.CONVERSATION;
                            ParentService.openMessenger();
                            if (ConversationService.getCurrentConversationId() || ConversationService.getLastConversationId()) {
                                destParams = {
                                    id: ConversationService.getCurrentConversationId() || ConversationService.getLastConversationId()
                                };
                            }
                            else {
                                // new
                                destParams = { new: true };
                            }
                            CoreService.push(destView, destParams);
                        }
                    }, 1000);
                    break;
                default:
                    this.$logger.warn('action ' + initAction + ' not supported');
            }
        }
        else {
            // navigate
            // CoreService.push(destView, destParams)
        }
        // testing
        // CoreService.push(Views.MAILFORM)
        // CoreService.push('FloatingConversation')
        // send hook to js
        // initialise route
        // console.log(i18n.global.getLocaleMessage('en'))
        //     i18n.global.setLocaleMessage('en',{ home: { text: 'jajaja' } })
        setTimeout(() => {
            ParentService.setMessengerLoaded();
            // ParentService.showAlert('<b>Thijs</b> from Grasp', 'Welcome to Grasp! How can I help you?');
        }, 4000);
        // setInterval(() => console.log(ParentService.isWindowFocused()), 2000)
        // push to to conversation view automatically on startup (handy while working on the conversation view)
        // this.$router.push({name: 'Conversation', params: { id: '1234567890' } })
    },
};
